// ============================================================================
// PackagePanel — this client's prepaid training packages: progress (consumed /
// remaining + value + expiry) and a create/cancel form. Mirrors SessionsPanel's
// shell (Card/CardHeader/EmptyState/Button + useTranslation("packages")).
// Money IS shown here (trainer-facing). Data: useClientPackages (live).
// ============================================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DateField,
  EmptyState,
  SelectField,
  TextField,
} from "../../components";
import type { SelectOption } from "../../components";
import { LoadingBlock } from "../../app/PageState";
import { formatMoney, SUPPORTED_CURRENCIES } from "../../lib/currency";
import {
  useClientPackages,
  useCreatePackage,
  useCancelPackage,
} from "../../hooks/packages";
import type { PackageInput, PackageProgress } from "../../api/types";

interface PackagePanelProps {
  clientId: number;
  defaultCurrency?: string | null;
}

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

export function PackagePanel({ clientId, defaultCurrency }: PackagePanelProps) {
  const { t } = useTranslation("packages");
  const query = useClientPackages(clientId);
  const create = useCreatePackage();
  const [open, setOpen] = useState(false);

  const packages = query.data ?? [];

  return (
    <Card>
      <CardHeader
        title={t("title")}
        subtitle={t("subtitle")}
        action={
          <Button size="sm" onClick={() => setOpen((v) => !v)}>
            {t("new")}
          </Button>
        }
      />
      {open && (
        <PackageForm
          clientId={clientId}
          defaultCurrency={defaultCurrency}
          busy={create.isPending}
          onSubmit={(body) =>
            create.mutate(
              { clientId, body },
              { onSuccess: () => setOpen(false) },
            )
          }
          onCancel={() => setOpen(false)}
        />
      )}
      {query.isLoading ? (
        <LoadingBlock />
      ) : packages.length === 0 ? (
        <EmptyState title={t("empty")} className="!py-10" />
      ) : (
        <ul className="space-y-3">
          {packages.map((p) => (
            <PackageRow key={p.id} pkg={p} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function PackageRow({ pkg }: { pkg: PackageProgress }) {
  const { t } = useTranslation("packages");
  const cancel = useCancelPackage();
  const remainingValue = formatMoney(pkg.remaining_value, pkg.currency, {
    symbol: pkg.currency_symbol,
  });
  // Plan uses "muted" tone which doesn't exist — use "neutral" (closest neutral).
  const tone: "neutral" | "ember" | "success" =
    pkg.status === "cancelled"
      ? "neutral"
      : pkg.reminder?.tone === "amber"
        ? "ember"
        : "success";
  return (
    <li className="rounded-md bg-surface p-3 ring-1 ring-line">
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-sm font-bold text-body">
          {pkg.name || t("title")}
        </span>
        <Badge tone={tone}>
          {pkg.status === "cancelled" ? t("cancelled") : `${pkg.pct}%`}
        </Badge>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-pill bg-surface-sunken">
        <span
          className="block h-full bg-grad-coral"
          style={{ width: `${pkg.pct}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-body-soft">
        <span>{t("progress", { consumed: pkg.consumed, total: pkg.total_sessions })}</span>
        {pkg.booked > 0 && <span>{t("booked", { count: pkg.booked })}</span>}
        {remainingValue && <span>{t("remainingValue", { value: remainingValue })}</span>}
        {pkg.expires_on && (
          <span
            className={
              pkg.reminder?.tone === "amber" ? "font-bold text-ember-600" : ""
            }
          >
            {t("expiresLabel", { date: pkg.expires_on })}
          </span>
        )}
      </div>
      {pkg.status !== "cancelled" && (
        <div className="mt-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => cancel.mutate(pkg.id)}
            disabled={cancel.isPending}
          >
            {t("cancel")}
          </Button>
        </div>
      )}
    </li>
  );
}

function PackageForm({
  defaultCurrency,
  busy,
  onSubmit,
  onCancel,
}: {
  clientId: number;
  defaultCurrency?: string | null;
  busy: boolean;
  onSubmit: (body: PackageInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("packages");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency || "HKD");
  const [amount, setAmount] = useState("");
  const [sessions, setSessions] = useState("");
  const [startsOn, setStartsOn] = useState(TODAY_ISO());
  const [expiresOn, setExpiresOn] = useState("");

  const currencyOptions: SelectOption[] = SUPPORTED_CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }));

  const submit = () => {
    const body: PackageInput = {
      name: name.trim() || null,
      currency,
      total_amount: Number(amount),
      total_sessions: Number(sessions),
      starts_on: startsOn,
      expires_on: expiresOn || null,
    };
    if (!body.total_amount || !body.total_sessions || body.total_sessions <= 0) return;
    onSubmit(body);
  };

  return (
    <div className="mb-4 space-y-3 rounded-md bg-surface-sunken/40 p-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <TextField
            label={t("name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <TextField
          label={t("amount")}
          type="number"
          inputMode="decimal"
          min={0}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <SelectField
          label={t("currency")}
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          options={currencyOptions}
        />
        <TextField
          label={t("sessions")}
          type="number"
          inputMode="numeric"
          min={1}
          value={sessions}
          onChange={(e) => setSessions(e.target.value)}
        />
        <DateField
          label={t("startsOn")}
          value={startsOn}
          onChange={setStartsOn}
        />
        <div className="col-span-2">
          <DateField
            label={t("expiresOn")}
            value={expiresOn}
            onChange={setExpiresOn}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy}>
          {t("save")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t("cancel")}
        </Button>
      </div>
    </div>
  );
}
